/**
 * Store pour la gestion de la rédaction de tâche
 */

import { ref, computed } from 'vue';
import { defineStore } from 'pinia';
import axios from '@js/tools/axios';

export const useWriteTaskStore = defineStore('writeTask', (() => {

    // State de la tâche courante en cours de rédaction
    const current = ref(null);

    // State de la liste des tâches
    const tasks = ref([]);

    /**
     * Retourne la tâche dont l'identifiant public est en paramètre
     * @return Object|null
     */
    const getByPublicId = computed(() => {
        return ((publicId) => {
            const index = tasks.value.findIndex((currentTask) => currentTask.publicId === publicId);
            if(index === -1) {
                return null;
            }
            return tasks.value[index];
        });
    });

    /**
     * Retourne la tâche courante en cours de rédaction
     * @return Object
     */
    const getCurrent = computed(() => {

        if(current.value === null) {
            setCurrent(getNewTaskData());
        }

        return current.value;
    });

    /**
     * Modifie la tâche courante
     * @param Object task
     * @return void
     */
    const setCurrent = ((task) => {
        current.value = task;
    });

    /**
     * Retourne une tâche initialisée pour une nouvelle rédaction
     * @return object
     */
    const getNewTaskData = (() => {
        return {
            category: null,
            deliveryEventsEstimated: [],
            briefing: {
                currentStep: {
                    step: 'idea',
                    subStep: 'introduction'
                },
                allowedSteps: window.AppConfig.task.write.steps,
                ideaType: null,
                networks: {}
            }
        };
    });

    /**
     * Chargement de la tâche dont l'identifiant public est en paramètre
     * @param string publicId
     * @return Promise
     */
    const loadTaskByPublicId = ((publicId) => {

        const path = '/api/me/tasks/:publicId'.replace(':publicId', publicId);

        return axios.get(path).then((response) => {
            if(response && response.data && response.data.success && response.data.task) {
                updateTask(response.data.task);
                return Promise.resolve();
            }
            return Promise.reject();
        });
    });

    /**
     * Enregistrement du type d'idée de la tâche (avec ou sans)
     * @param string type with|without
     * @return Promise
     */
    const updateCurrentIdeaType = ((type) => {

        const currentTask = getCurrent.value;

        let path = '/api/me/tasks';

        if(currentTask.publicId !== undefined) {
            path += '/:taskId/update-idea-type'.replace(':taskId', currentTask.publicId);
        }

        return axios.post(path, {
            idea: type
        }).then((response) => {
            if(response && response.data && response.data.success && response.data.task) {
                setCurrent(response.data.task);
                updateTask(response.data.task);
                return Promise.resolve();
            }
            return Promise.reject();
        });
    });

    /**
     * Enregistrement du type de tâche du briefing (complet, visuel, accroche)
     * @param string type
     * @return Promise
     */
    const updateBriefingTaskType = ((type) => {

        const currentTask = getCurrent.value;

        const path = '/api/me/tasks/:taskId/update-task-type'.replace(':taskId', currentTask.publicId);

        return axios.post(path, {
            type: type
        }).then((response) => {
            if(response && response.data && response.data.success && response.data.task) {
                setCurrent(response.data.task);
                updateTask(response.data.task);
                return Promise.resolve();
            }
            return Promise.reject();
        });
    });

    /**
     * Active le réseau pour la tâche en paramètre
     * @param string network
     * @return Proimise
     */
    const enableNetwork = ((network) => {

        const currentTask = getCurrent.value;

        const path = '/api/me/tasks/:taskId/networks/:network'
            .replace(':taskId', currentTask.publicId)
            .replace(':network', network)
        ;

        return axios.post(path).then((response) => {
            if(response && response.data && response.data.success && response.data.task) {
                setCurrent(response.data.task);
                updateTask(response.data.task);
                return Promise.resolve();
            }
            return Promise.reject();
        });

    });

    /**
     * Désactive le réseau pour la tâche en paramètre
     * @param string network
     * @return Proimise
     */
    const disableNetwork = ((network) => {

        const currentTask = getCurrent.value;

        const path = '/api/me/tasks/:taskId/networks/:network'
            .replace(':taskId', currentTask.publicId)
            .replace(':network', network)
        ;

        return axios.delete(path).then((response) => {
            if(response && response.data && response.data.success && response.data.task) {
                setCurrent(response.data.task);
                updateTask(response.data.task);
                return Promise.resolve();
            }
            return Promise.reject();
        });

    });

    /**
     * Active ou désactive le compte du réseau en paramètre pour la tâche courante
     * @param string network (twitter, facebook)
     * @param object account Le compte à activer ou désactiver
     * @return Promise
     */
    const toggleEnableNetworkAccount = ((network, account) => {

        const currentTask = getCurrent.value;

        const path = '/api/me/tasks/:taskId/networks/:network/accounts/:account/toggle-enable'
            .replace(':taskId', currentTask.publicId)
            .replace(':network', network)
            .replace(':account', account.id) // /!\ Correspond à un identifiant d'API du réseau
        ;

        return axios.post(path).then((response) => {
            if(response && response.data && response.data.success && response.data.task) {
                setCurrent(response.data.task);
                updateTask(response.data.task);
                return Promise.resolve();
            }
            return Promise.reject();
        });
    });

    /**
     * Finalise l'étape de sélection des comptes des réseaux sociaux
     * @return Promise
     */
    const completeSocialNetworkStep = (() => {

        const currentTask = getCurrent.value;

        const path = '/api/me/tasks/:taskId/networks/finalize'
            .replace(':taskId', currentTask.publicId)
        ;

        return axios.post(path).then((response) => {
            if(response && response.data && response.data.success && response.data.task) {
                setCurrent(response.data.task);
                updateTask(response.data.task);
                return Promise.resolve();
            }
            return Promise.reject();
        });
    });

    /**
     * Modifie le format de la tâche
     * @return Promise
     */
    const updateFormat = ((format) => {

        const currentTask = getCurrent.value;

        const path = '/api/me/tasks/:taskId/format/update'
            .replace(':taskId', currentTask.publicId)
        ;

        return axios.post(path, {
            format: format.type
        }).then((response) => {
            if(response && response.data && response.data.success && response.data.task) {
                setCurrent(response.data.task);
                updateTask(response.data.task);
                return Promise.resolve();
            }
            return Promise.reject();
        });
    });

    /**
     * Met à jour le réglage de l'accroche (COMMON, UNIQUE)
     * @param string setting
     * @return Promise
     */
    const updateHookSetting = ((setting) => {

        const currentTask = getCurrent.value;

        const path = '/api/me/tasks/:taskId/update-hook-setting'
            .replace(':taskId', currentTask.publicId)
        ;

        return axios.post(path, {
            setting: setting
        }).then((response) => {
            if(response && response.data && response.data.success && response.data.task) {
                setCurrent(response.data.task);
                updateTask(response.data.task);
                return Promise.resolve();
            }
            return Promise.reject();
        });

    });

    /**
     * Finalise l'étape de sélection du format
     * @return Promise
     */
    const completeFormatStep = (() => {

        const currentTask = getCurrent.value;

        const path = '/api/me/tasks/:taskId/format/finalize'
            .replace(':taskId', currentTask.publicId)
        ;

        return axios.post(path).then((response) => {
            if(response && response.data && response.data.success && response.data.task) {
                setCurrent(response.data.task);
                updateTask(response.data.task);
                return Promise.resolve();
            }
            return Promise.reject();
        });
    });

    /**
     * Modifie la catégorie de la tâche
     * @param object params
     * @return Promise
     */
    const updateCategory = ((params) => {
        const currentTask = getCurrent.value;

        const path = '/api/me/tasks/:taskId/category'
            .replace(':taskId', currentTask.publicId)
        ;

        return axios.post(path, params).then((response) => {
            if(response && response.data && response.data.success && response.data.task) {
                setCurrent(response.data.task);
                updateTask(response.data.task);
                return Promise.resolve({});
            }
            return Promise.reject({});
        }).catch((response) => {
            const data = (response && response.response && response.response.data) ? response.response.data : {};
            const errors = data.errors || {};
            return Promise.reject(errors);
        });
    });

    /**
     * Modifie l'objectif marketing de la tâche
     * @param string objective
     * @return Promise
     */
    const changeMarketingObjective = ((objective) => {
        const currentTask = getCurrent.value;

        const path = '/api/me/tasks/:taskId/marketing/objective'
            .replace(':taskId', currentTask.publicId)
        ;

        return axios.post(path, {
            objective: objective
        }).then((response) => {
            if(response && response.data && response.data.success && response.data.task) {
                setCurrent(response.data.task);
                updateTask(response.data.task);
                return Promise.resolve();
            }
            return Promise.reject();
        });
    });

    /**
     * Modifie le type de visuel souhaité pour la tâche
     * @param string $type
     * @return Promise
     */
    const updateGraphType = ((type) => {
        const currentTask = getCurrent.value;

        const path = '/api/me/tasks/:taskId/graph/type'
            .replace(':taskId', currentTask.publicId)
        ;

        return axios.post(path, {
            type: type
        }).then((response) => {
            if(response && response.data && response.data.success && response.data.task) {
                setCurrent(response.data.task);
                updateTask(response.data.task);
                return Promise.resolve();
            }
            return Promise.reject();
        });
    });

    /**
     * Modifie les paramètres d'utilisation de la charte graphique
     * @param object params Paramètre à transmettre en Ajax
     * @return Promise
     */
    const updateGraphSettings = ((params) => {
        const currentTask = getCurrent.value;

        const path = '/api/me/tasks/:taskId/graph/settings'
            .replace(':taskId', currentTask.publicId)
        ;

        return axios.post(path, params).then((response) => {
            if(response && response.data && response.data.success && response.data.task) {
                setCurrent(response.data.task);
                updateTask(response.data.task);
                return Promise.resolve();
            }
            return Promise.reject();
        });
    });

    /**
     * Alterne la priorité entre le mode urgent et normal
     * @return Promise
     */
    const togglePriorityStatus = (() => {
        const currentTask = getCurrent.value;

        const path = '/api/me/tasks/:taskId/priority/toggle'
            .replace(':taskId', currentTask.publicId)
        ;

        return axios.post(path).then((response) => {
            if(response && response.data && response.data.success && response.data.task) {
                setCurrent(response.data.task);
                updateTask(response.data.task);
                return Promise.resolve();
            }
            return Promise.reject();
        });
    });

    /**
     * Modifie la priorité de la tâche courante
     * @param string newPriority
     * @return Promise
     */
    const updatePriorityStatus = ((newPriority) => {
        const currentTask = getCurrent.value;

        const path = '/api/me/tasks/:taskId/priority/update'
            .replace(':taskId', currentTask.publicId)
        ;

        return axios.post(path, {
            priority: newPriority
        }).then((response) => {
            if(response && response.data && response.data.success && response.data.task) {
                setCurrent(response.data.task);
                updateTask(response.data.task);
                return Promise.resolve();
            }
            return Promise.reject();
        });
    });

    /**
     * Mise à jour de la vidéo sélectionnée pour la tâche courante
     * @param object video
     * @return Promise
     */
    const updateCurrentVideo = ((video) => {
        const currentTask = getCurrent.value;

        const path = '/api/me/tasks/:taskId/graph/update-video'
            .replace(':taskId', currentTask.publicId)
        ;

        return axios.post(path, {
            video_id: video.id
        }).then((response) => {
            if(response && response.data && response.data.success && response.data.task) {
                setCurrent(response.data.task);
                updateTask(response.data.task);
                return Promise.resolve();
            }
            return Promise.reject();
        });
    });

    /**
     * Sélection de la suggestion de publication pour la tâche courante
     * @param object suggestion
     * @return Promise
     */
    const selectPublicationSuggestion = ((suggestion) => {

        const currentTask = getCurrent.value;

        const path = '/api/me/tasks/:taskId/select-publication-suggestion'
            .replace(':taskId', currentTask.publicId)
        ;

        return axios.post(path, {
            suggestion_id: suggestion.id
        }).then((response) => {
            if(response && response.data && response.data.success && response.data.task) {
                setCurrent(response.data.task);
                updateTask(response.data.task);
                return Promise.resolve();
            }
            return Promise.reject();
        });
    });

    /**
     * Mise à jour de la tâche dans les tâches déjà chargées
     * @param object task
     * @return void
     */
    const updateTask = ((task) => {

        const index = tasks.value.findIndex((currentTask) => currentTask.publicId === task.publicId);
        if(index === -1) {
            tasks.value.push(task);
        } else {
            tasks.value[index] = task;
        }

    });

    /**
     * Déplacement vers l'étape en paramètre
     * @param string step
     * @param string subStep
     * @return Promise
     */
    const changingStep = ref(false);
    const goToStep = ((step, subStep) => {

        if(changingStep.value) {
            return Promise.reject();
        }

        changingStep.value = true;

        const currentTask = getCurrent.value;

        const path = '/api/me/tasks/:taskId/steps/changing'
            .replace(':taskId', currentTask.publicId)
        ;

        return axios.post(path, {
            step: step,
            subStep: subStep
        }).then((response) => {
            if(response && response.data && response.data.success && response.data.task) {
                setCurrent(response.data.task);
                updateTask(response.data.task);
                return Promise.resolve();
            }
            return Promise.reject();
        }).finally(() => {
            changingStep.value = false;
        });

    });

    return {
        getCurrent,
        getByPublicId,
        /***/
        loadTaskByPublicId,
        /***/
        setCurrent,
        updateTask,
        /***/
        updateCurrentIdeaType,
        updateBriefingTaskType,
        disableNetwork,
        enableNetwork,
        toggleEnableNetworkAccount,
        completeSocialNetworkStep,
        updateFormat,
        updateHookSetting,
        completeFormatStep,
        updateCategory,
        changeMarketingObjective,
        updateGraphType,
        updateGraphSettings,
        togglePriorityStatus,
        updatePriorityStatus,
        updateCurrentVideo,
        selectPublicationSuggestion,
        /***/
        goToStep
    };
}));
